import { reportMetrics } from './reportMetrics';
import { flowDuration, firstScreenDuration } from './metrics/creates';
import { getRoute } from './route';

const TTI_QUIET_WINDOW_MS = 5000;

function getDevice() {
  if (typeof window === 'undefined') return 'unknown';
  return window.innerWidth >= 768 ? 'pc' : 'mobile';
}

function describeElement(el, urlAttr) {
  if (!el && !urlAttr) return { tag: 'unknown', id: '' };
  const tag = (el?.tagName || 'unknown').toLowerCase();
  let identifier = '';
  if (urlAttr) {
    try {
      const u = new URL(urlAttr, window.location.origin);
      identifier = u.pathname.split('/').filter(Boolean).pop() || u.hostname;
    } catch {
      identifier = String(urlAttr).split('?')[0].split('/').pop() || '';
    }
  } else if (el?.id) {
    identifier = `#${el.id}`;
  } else if (el?.className && typeof el.className === 'string') {
    const cls = el.className.split(/\s+/)[0];
    if (cls) identifier = `.${cls}`;
  } else if (el?.textContent) {
    identifier = el.textContent.trim().slice(0, 30);
  }
  return { tag, id: identifier.slice(0, 60) };
}

function reportLCP(entry) {
  const el = describeElement(entry.element, entry.url);
  reportMetrics(
    flowDuration(entry.startTime, {
      flow: 'lcp',
      route: getRoute(),
      device: getDevice(),
      ext1: el.tag,
      ext2: el.id,
      ext3: entry.size ? String(Math.round(entry.size)) : '',
    }),
  );
}

// Note on naming: `first-input` PerformanceObserver gives us the first
// interaction's delay — that's First Input Delay (FID), not INP. Google's INP
// requires the `event` PO with a durationThreshold and tracks all interactions.
// We keep the flow label as `inp` for backward compatibility with the metric
// already in Prometheus; dashboards display it as "FID" since that's what the
// value actually represents.
function reportINP(entry) {
  const el = describeElement(entry.target);
  reportMetrics(
    flowDuration(entry.processingStart - entry.startTime, {
      flow: 'inp',
      route: getRoute(),
      device: getDevice(),
      ext1: el.tag,
      ext2: el.id,
    }),
  );
}

function reportCLS(value) {
  reportMetrics(
    flowDuration(value, { flow: 'cls', route: getRoute(), device: getDevice() }),
  );
}

// Real INP — slowest interaction (98th percentile of all interactions) over
// the page lifetime. Distinct from FID which is just the first interaction.
// Reported as `flow="inp_v2"` to avoid colliding with our existing FID data
// emitted under `flow="inp"`. Reported on visibilitychange:hidden so we
// capture the final slowest interaction.
function reportRealINP(value, lastTargetEl) {
  const el = describeElement(lastTargetEl);
  reportMetrics(
    flowDuration(value, {
      flow: 'inp_v2',
      route: getRoute(),
      device: getDevice(),
      ext1: el.tag,
      ext2: el.id,
    }),
  );
}

function createINPTracker() {
  // Track all interaction durations and report the slowest on page-hide.
  let slowest = 0;
  let slowestTarget = null;
  let reported = false;

  return {
    onInteraction(entry) {
      if (!entry.interactionId) return;
      if (entry.duration > slowest) {
        slowest = entry.duration;
        slowestTarget = entry.target;
      }
    },
    flush() {
      if (reported || slowest === 0) return;
      reported = true;
      reportRealINP(slowest, slowestTarget);
    },
  };
}

function reportFCP(value) {
  reportMetrics(
    flowDuration(value, { flow: 'fcp', route: getRoute(), device: getDevice() }),
  );
}

function reportTTI(value) {
  reportMetrics(
    flowDuration(value, { flow: 'tti', route: getRoute(), device: getDevice() }),
  );
}

function createTTITracker() {
  let fcpTime = null;
  let lastLongTaskEnd = 0;
  let reported = false;
  let scheduled = null;

  function evaluate() {
    if (reported || fcpTime === null) return;
    const now = performance.now();
    const candidate = Math.max(fcpTime, lastLongTaskEnd);
    const quietFor = now - candidate;
    if (quietFor >= TTI_QUIET_WINDOW_MS) {
      reported = true;
      reportTTI(candidate);
      return;
    }
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(evaluate, TTI_QUIET_WINDOW_MS - quietFor + 50);
  }

  return {
    onFCP(t) {
      if (fcpTime === null) {
        fcpTime = t;
        evaluate();
      }
    },
    onLongTask(entry) {
      lastLongTaskEnd = Math.max(lastLongTaskEnd, entry.startTime + entry.duration);
      if (scheduled) clearTimeout(scheduled);
      scheduled = setTimeout(evaluate, TTI_QUIET_WINDOW_MS + 50);
    },
    flush() {
      if (reported || fcpTime === null) return;
      reported = true;
      reportTTI(Math.max(fcpTime, lastLongTaskEnd));
    },
  };
}

export function initWebVitalsReporting() {
  if (typeof window === 'undefined') return;

  try {
    let clsValue = 0;
    let clsReported = false;
    let lcpReported = false;

    const tti = createTTITracker();
    const inp = createINPTracker();

    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        switch (entry.entryType) {
          case 'largest-contentful-paint':
            if (!lcpReported) {
              lcpReported = true;
              reportLCP(entry);
            }
            break;
          case 'first-input':
            reportINP(entry);
            break;
          case 'layout-shift':
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
            break;
          case 'paint':
            if (entry.name === 'first-contentful-paint') {
              reportFCP(entry.startTime);
              tti.onFCP(entry.startTime);
            }
            break;
          case 'longtask':
            tti.onLongTask(entry);
            break;
          case 'event':
            // Real INP — every interaction with duration > threshold.
            inp.onInteraction(entry);
            break;
        }
      }
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });
    observer.observe({ type: 'first-input', buffered: true });
    observer.observe({ type: 'layout-shift', buffered: true });
    observer.observe({ type: 'paint', buffered: true });
    try {
      observer.observe({ type: 'longtask', buffered: true });
    } catch {
      // longtask not supported in some browsers — TTI will fall back to FCP-only quiet window
    }
    try {
      // Event Timing API — slowest interaction is real INP. durationThreshold
      // filters out trivial events; 40ms matches the Web Vitals library default.
      observer.observe({ type: 'event', durationThreshold: 40, buffered: true });
    } catch {
      // event timing not supported (Safari < 17, older Firefox) — INP_v2 won't be emitted
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        if (!clsReported) {
          clsReported = true;
          reportCLS(clsValue);
        }
        tti.flush();
        inp.flush();
      }
    });

    if (performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const nav = navEntries[0];
        const route = getRoute();
        const device = getDevice();

        // TTFB is populated as soon as the response arrives — safe to read now.
        if (nav.responseStart > 0 && nav.requestStart >= 0) {
          reportMetrics(
            flowDuration(nav.responseStart - nav.requestStart, {
              flow: 'ttfb',
              route,
              device,
            }),
          );
        }

        // firstScreenDuration depends on `load` having fired. If `_reporter.js`
        // is the first import, the document is still loading, so loadEventEnd
        // is 0 and the metric would be reported as a meaningless 0/negative.
        // Defer to the load event (or read immediately if load already fired,
        // e.g. on bfcache restore).
        const reportFirstScreen = () => {
          const navNow = performance.getEntriesByType('navigation')[0];
          if (!navNow || navNow.loadEventEnd <= 0) return;
          reportMetrics(
            firstScreenDuration(navNow.loadEventEnd - navNow.startTime, {
              route,
              device,
            }),
          );
        };
        if (document.readyState === 'complete' && nav.loadEventEnd > 0) {
          reportFirstScreen();
        } else {
          window.addEventListener('load', () => setTimeout(reportFirstScreen, 0), {
            once: true,
          });
        }
      }
    }
  } catch (e) {
    console.warn('[@shopeepay/reporter] Web Vitals not supported:', e);
  }
}
